<script setup lang="ts">
/**
 * RejectRecallModal — Collects a mandatory rejection reason before rejecting a recall.
 *
 * Opens from ViewRecallModal when the user clicks "Reject Recall Request".
 * The confirmation button is disabled until a reason is provided.
 */
import { ref, computed, watch } from 'vue';
import { DialogModal } from '@tms-phoenix/ui-vue';
import Button from 'primevue/button';
import Textarea from 'primevue/textarea';
import type { Recall } from '../types';

const props = defineProps<{
  visible: boolean;
  recall: Recall | null;
}>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'confirm', recall: Recall, reason: string): void;
}>();

const reason = ref('');
const touched = ref(false);

const isValid = computed(() => reason.value.trim().length > 0);

function resetForm() {
  reason.value = '';
  touched.value = false;
}

// Reset form whenever the modal is opened
watch(
  () => props.visible,
  (isOpen) => {
    if (isOpen) resetForm();
  },
);

function handleConfirm() {
  touched.value = true;
  if (!isValid.value || !props.recall) return;
  emit('confirm', props.recall, reason.value.trim());
  resetForm();
}

function handleCancel() {
  resetForm();
  emit('update:visible', false);
}
</script>

<template>
  <DialogModal
    :visible="visible"
    :title="$t('recall.rejectModal.title')"
    max-width="max-w-md"
    position="center"
    :show-footer="false"
    intercept-close
    @update:visible="emit('update:visible', $event)"
    @close="handleCancel"
  >
    <div class="flex flex-col gap-4">
      <p class="text-sm text-gray-600">
        {{ $t('recall.rejectModal.instruction') }}
      </p>

      <div class="flex flex-col gap-1.5">
        <label
          for="reject-reason"
          class="text-sm font-medium text-gray-700"
        >
          {{ $t('donor.approval.rejectionReason') }} <span class="text-red-500">*</span>
        </label>
        <Textarea
          id="reject-reason"
          v-model="reason"
          rows="4"
          :placeholder="$t('recall.rejectModal.reasonPlaceholder')"
          :class="[
            'w-full rounded-lg border text-sm',
            touched && !isValid ? 'border-red-400' : 'border-gray-300',
          ]"
          @blur="touched = true"
        />
        <span
          v-if="touched && !isValid"
          class="text-xs text-red-500"
        >
          {{ $t('recall.rejectModal.reasonRequired') }}
        </span>
      </div>

      <div class="flex items-center gap-3 border-t border-gray-200 pt-4">
        <Button
          :label="$t('recall.rejectModal.confirmButton')"
          severity="danger"
          :disabled="!isValid"
          @click="handleConfirm"
        />
        <Button
          :label="$t('common.cancel')"
          severity="secondary"
          outlined
          @click="handleCancel"
        />
      </div>
    </div>
  </DialogModal>
</template>
